/**
 * In-memory cache of photo references the browser has already loaded
 * in this SPA session. Two consumers:
 *
 *   - `markPhotoLoaded(ref)` — invoked from `<img onLoad>` so we
 *     remember the photo has rendered at least once.
 *   - `isPhotoLoaded(ref)` — read-only check; useful if a component
 *     wants to skip a skeleton/spinner on a re-mount because we
 *     already know the browser has the bytes cached.
 *
 * Why this exists alongside the HTTP cache: the backend's
 * /api/photo endpoint now streams image bytes directly (cached in
 * MongoDB for 90 days, see FIX 1 in the cost-incident spec). The
 * browser caches the response for 7 days via Cache-Control, but
 * React can unmount and re-mount <img> tags across navigations,
 * and some browsers issue a conditional request even on a cache
 * hit. This Set lets us avoid re-rendering placeholders for
 * already-loaded refs.
 *
 * Lives at module scope (not in React state) so it survives
 * component remounts but is wiped on full page refresh.
 */
const loadedRefs = new Set();

export const markPhotoLoaded = (ref) => {
  if (ref) loadedRefs.add(ref);
};

export const isPhotoLoaded = (ref) => loadedRefs.has(ref);

/**
 * Module-scoped Map of URL → in-flight Promise<Blob>. When two cards
 * mount at the same time and request the same photo URL, we make
 * ONE network request and hand the same resolved Blob to both —
 * eliminating the duplicate-photo-fetch class of bugs the cost
 * incident spec flagged (search list with 10 places re-rendering 3x
 * used to fire 30 requests instead of 10).
 *
 * Most consumers don't need to use this directly — they should use
 * stable React keys on place cards (tied to place_id) and a plain
 * <img src={photoUrl(ref)} loading="lazy" /> tag. The browser HTTP
 * cache handles the rest. This Map is here for components that need
 * Blob URLs (e.g., the carousel preloader) and want to share work.
 */
const inflightPhotoFetches = new Map();

export const fetchPhotoOnce = (url) => {
  if (!url) return Promise.resolve(null);
  const existing = inflightPhotoFetches.get(url);
  if (existing) return existing;
  const p = fetch(url, { credentials: "omit" })
    .then((r) => (r.ok ? r.blob() : null))
    .catch(() => null)
    .finally(() => {
      // Keep the entry in the map AFTER resolution — same URL during
      // the same session should never refetch. The HTTP cache layer
      // is the source of truth past session boundary.
      // Note: we don't delete; the fetched Blob promise is durable.
    });
  inflightPhotoFetches.set(url, p);
  return p;
};

/**
 * Construct a stable URL for a Google Places photo via our /api/photo
 * proxy. Keeping URL construction here ensures every consumer hits
 * the same path with the same width semantics — small refactor wins
 * but the cost-of-being-wrong (extra Google Photo API calls due to
 * URL drift) makes this worth centralizing.
 */
export const photoUrl = (ref, w = 600) => {
  if (!ref) return null;
  // Absolute paths/URLs (e.g. /api/static/manual_photos/.../img.jpg)
  // bypass the Google photo proxy — they're already final URLs.
  if (ref.startsWith("http") || ref.startsWith("/")) return ref;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}/api/photo?ref=${encodeURIComponent(ref)}&w=${w}`;
};
